import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import API from "../../utils/axios";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { withSnackbar } from "notistack";

import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import makeStyles from "@material-ui/core/styles/makeStyles";
import clsx from "clsx";
import { remCalc } from "../../utils/helpers";
import TextField from "@material-ui/core/TextField";
import UnsavedChangesDialog from "./UnsavedChangesDialog";
import LinkIcon from "@material-ui/icons/Link";
import IconButton from "@material-ui/core/IconButton";
import { fade } from "@material-ui/core/styles/colorManipulator";
import { maxNoteLength } from "../data/constants";
import useProductTour, {
  PRODUCT_TOUR_STEP_IDS
} from "../../states/tour/tours/product";
import { Grid } from "@material-ui/core";

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: "1.125rem",
    lineHeight: "1.3125rem",
    fontWeight: 500,
    color: "#202124"
  },
  inputField: {
    width: "100%",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    minHeight: theme.spacing(7.75),
    fontSize: "0.9375rem"
  },
  inputLabel: {
    fontSize: "0.9375rem"
  },
  customHelperText: {
    fontSize: "0.6875rem",
    lineHeight: "0.8125rem",
    letterSpacing: 0
  },
  submitButton: {
    textTransform: "none",
    backgroundColor: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: [fade(theme.palette.primary.main, 0.7), "!important"]
    }
  },
  cancelButton: {
    backgroundColor: "transparent !important"
  },
  copyButton: {
    backgroundColor: theme.palette.primary.main,
    marginLeft: theme.spacing(1),

    "&:hover": {
      backgroundColor: fade(theme.palette.primary.main, 0.7)
    },

    "&.Mui-disabled": {
      backgroundColor: "rgba(0, 0, 0, 0.2)"
    }
  },
  copyToClipboardText: {
    fontSize: remCalc(13)
  },
  copyToClipboardTextActive: {
    color: theme.palette.primary.main,
    cursor: "pointer"
  }
}));

function FormDialog({
  open,
  title,
  label,
  dataCy,
  placeholder,
  handleClose,
  isShareRoute,
  action,
  value,
  setValue,
  cancelText = "Cancel",
  confirmText,
  routeIds,
  project,
  routes,
  enqueueSnackbar,
  id,
  cancelDisabled,
  confirmId,
  disableEscapeKeyDown,
  errorText
}) {
  const classes = useStyles();

  const [url, setUrl] = useState("");
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [changesDialog, setChangesDialog] = useState(false);

  const productTour = useProductTour();

  const handleChange = e => {
    setValue(
      isShareRoute ? e.target.value : e.target.value.slice(0, maxNoteLength)
    );
    setSaveDisabled(false);
  };

  // Copy success
  const onCopySuccess = () => {
    enqueueSnackbar("Link copied to clipboard");
  };

  // Set driver url
  useEffect(() => {
    const route = routes.find(el => el.id === routeIds[0]);

    if (route) {
      // Set email
      handleChange({
        target: { value: route.driver ? route.driver.email : "" }
      });

      // Fetch and set driver url
      if (routeIds.length === 1) {
        API.post(`/v1/projects/${project.id}/routes/${routeIds[0]}/send/email`)
          .then(res => {
            setUrl(res.data);
          })
          .catch(error => console.log(error));
      }
    } else {
      setUrl("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeIds]);

  // Warn before closing
  const warnClose = () => {
    const route = routes.find(el => el.id === routeIds[0]);
    if (
      isShareRoute &&
      route &&
      route.driver &&
      route.driver.email &&
      route.driver.email !== value
    ) {
      setChangesDialog(true);
    } else {
      handleClose();
    }
  };

  useEffect(() => {
    if (
      productTour.isTourStepId(
        PRODUCT_TOUR_STEP_IDS.PART_PUBLISH_AND_SHARE_STEP_SHARE_DIALOG
      )
    ) {
      if (isShareRoute && !!value) {
        productTour.showPulsingBorder();
      } else {
        productTour.hidePulsingBorder();
      }
    }
  }, [isShareRoute, productTour, value]);

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      onClose={warnClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{ onSubmit: action, component: "form" }}
      disableEscapeKeyDown={disableEscapeKeyDown}
    >
      <div id={id}>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pt={3}
          pb={1.5}
          px={3}
        >
          <Typography variant="h5" className={classes.title}>
            {title}
          </Typography>
          {isShareRoute && routeIds.length === 1 && (
            <Box
              display="inline-flex"
              alignItems="center"
              className="step-send-clipboard"
            >
              <CopyToClipboard text={url} onCopy={onCopySuccess}>
                <Grid container alignItems="center">
                  <Grid item>
                    <Typography
                      variant="body2"
                      color="secondary"
                      className={clsx(
                        classes.copyToClipboardText,
                        url && classes.copyToClipboardTextActive
                      )}
                    >
                      Copy to clipboard
                    </Typography>
                  </Grid>
                  <Grid item>
                    <IconButton
                      size="small"
                      color="primary"
                      className={classes.copyButton}
                      disabled={!url}
                      data-cy="shareRoute-copyToClipboard"
                    >
                      <LinkIcon
                        style={{ color: "#fff", fontSize: "1.125rem" }}
                      />
                    </IconButton>
                  </Grid>
                </Grid>
              </CopyToClipboard>
            </Box>
          )}
        </Box>
        {/* Body */}
        <Box py={1} px={3}>
          <TextField
            value={value}
            className={clsx(classes.inputField)}
            onChange={handleChange}
            label={label}
            placeholder={placeholder}
            margin="normal"
            variant="outlined"
            inputProps={{ "data-cy": dataCy }}
            rowsMax="5"
            fullWidth
            multiline={!isShareRoute}
            error={errorText || value.length >= maxNoteLength}
            InputLabelProps={{
              required: false,
              className: classes.inputLabel
            }}
            FormHelperTextProps={{
              classes: {
                root: classes.customHelperText
              }
            }}
            helperText={
              errorText ? (
                errorText
              ) : (
                <span
                  style={{
                    display: "flex",
                    component: "span",
                    justifyContent: "space-between"
                  }}
                >
                  <span>
                    {value.length >= maxNoteLength &&
                      `Maximum allowed note length is ${maxNoteLength}`}
                  </span>
                  <span>{`${value.length}/${maxNoteLength}`}</span>
                </span>
              )
            }
          />
        </Box>

        {/* Actions */}
        <DialogActions>
          <Button
            onClick={warnClose}
            type="button"
            variant="text"
            disabled={cancelDisabled}
            className={classes.cancelButton}
          >
            {cancelText}
          </Button>

          <Button
            color="primary"
            type="submit"
            variant="contained"
            disabled={saveDisabled}
            id={confirmId}
            className={classes.submitButton}
          >
            {confirmText}
          </Button>
        </DialogActions>

        {/* Unsaved changes dialog */}
        <UnsavedChangesDialog
          open={changesDialog}
          handleClose={() => setChangesDialog(false)}
          action={handleClose}
        />
      </div>
    </Dialog>
  );
}

const mapStateToProps = state => ({
  project: state.project,
  routes: state.project.solution ? state.project.solution.routes : []
});

export default connect(mapStateToProps)(withSnackbar(FormDialog));
